import { useEffect, useState } from 'react'

import { Form, Select, Space } from 'antd'
import { useForm } from 'antd/es/form/Form'

import i18n from 'i18next'

import CommonModal from '@/components/common/CommonModal'
import Info from '@/components/common/Info'
import MultiSelectDropdown from '@/components/common/MultiSelectDropdown'

import { IColumn, IRow } from '@/interfaces/interfaces'

import { ActionProps, ActionTypes, DeletePositionAction } from './types'

export type DeletePositionForm = {
  axis: 0 | 1
  by: number[]
}

// `columns`/`rows` are the resulting (post-actions) output; `originalColumns`/`originalRows`
// are the untransformed table, used to label positions this action itself deleted.
// `transposed` says whether the output orientation is swapped relative to the original
// table, so the saved selection is looked up in the correct original axis.
type DeletePositionProps = ActionProps<DeletePositionAction> & {
  originalColumns?: IColumn[]
  originalRows?: IRow[]
  transposed?: boolean
}

const DeletePosition = ({
  columns = [],
  rows = [],
  originalColumns = [],
  originalRows = [],
  transposed = false,
  onSubmit,
  onCancel,
  isVisible,
  initialValues,
  clearOptions = false
}: DeletePositionProps) => {
  const initial = {
    axis: 1, // default: columns
    by: [],
    ...(initialValues || {})
  }
  const [form] = useForm<DeletePositionForm>()
  const [byLength, setByLength] = useState(initial.by.length)
  const [labelsVisible, setLabelsVisible] = useState(false)

  useEffect(() => {
    if (clearOptions === true) {
      setByLength(initial.by.length)
      form.resetFields()
      //@ts-ignore
      form.setFieldsValue(initial)
      onByChange(initial.by)
    }
  }, [clearOptions, initialValues])

  useEffect(() => {
    form.resetFields()
  }, [])

  useEffect(() => {
    setByLength(initial.by.length)
  }, [initialValues])

  const submitForm = async () => {
    try {
      await form.validateFields()
    } catch {
      // Validation failed (e.g. nothing selected); antd surfaces the field error and
      // we skip submitting so no invalid/stale action is sent.
      return
    }
    const { axis, by } = form.getFieldsValue(true)
    onSubmit?.({
      actionType: ActionTypes.DELETE_POSITION,
      axis,
      by
    })
  }

  const onAxisChange = (value: 0 | 1) => {
    form.setFieldsValue({ axis: value, by: [] })
    setByLength(0)
  }

  const onByChange = (value: number[]) => {
    form.setFieldsValue({ by: value })
    setByLength(form.getFieldsValue(true).by.length)
  }

  const popoverContent = (
    <div>
      <strong>Function</strong>
      <p>Delete selected rows or columns by their position in the output.</p>
      <strong>How to use</strong>
      <p>[Orientation]: Select whether you want to delete columns or rows.</p>
      <p>[To delete]: Select one or more rows/columns to remove from the output.</p>
      <strong>NOTES</strong>
      <p>
        <em>Only rows/columns currently present in the output can be deleted.</em>
      </p>
    </div>
  )

  return (
    <CommonModal
      visible={isVisible}
      onCancel={onCancel}
      onOk={submitForm}
      okDisabled={byLength < 1}
      title={
        <span>
          Delete by position <Info>{popoverContent}</Info>
        </span>
      }
    >
      <Form form={form} initialValues={initial} layout="vertical">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Form.Item name="axis" rules={[{ required: true }]} label="Orientation">
            <Select onChange={onAxisChange}>
              <Select.Option value={1}>
                {i18n.t('editor.right-panel.data-source.form.columns')}
              </Select.Option>
              <Select.Option value={0}>
                {i18n.t('editor.right-panel.data-source.form.rows')}
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            rules={[{ required: true }]}
            label="To delete"
            shouldUpdate={(prevValues, curValues) => prevValues.axis !== curValues.axis}
          >
            {({ getFieldValue }) => {
              const axis = getFieldValue('axis')
              const resultingOptions = axis === 0 ? rows : columns
              // resultingRows/Columns reflect the output after every action, including
              // the delete being edited, so positions this action removed are absent
              // here. Merge the saved selection back in (labelled from the original
              // table) so it stays visible/editable (PR #834 review). Only for the saved
              // axis — switching axis clears `by`. Under transpose the output axis maps
              // to the opposite raw axis, so the saved indices live in the swapped
              // original array.
              const useOriginalColumns = (axis === 1) !== transposed
              const originalOptions = useOriginalColumns ? originalColumns : originalRows
              const savedBy: number[] = axis === initial.axis ? initial.by : []
              const present = new Set(resultingOptions.map(({ index }) => index))
              // Present items keep their 1-based position in the current output; deleted
              // items merged back keep their 1-based position in the original table, so
              // the displayed position never derives from the combined-array index.
              const options = resultingOptions.map((opt, i) => ({ ...opt, position: i + 1 }))
              originalOptions.forEach((opt, i) => {
                if (savedBy.includes(opt.index) && !present.has(opt.index)) {
                  options.push({ ...opt, position: i + 1 })
                }
              })
              return (
                <Form.Item
                  name="by"
                  initialValue={initial.by}
                  rules={[
                    { required: true, message: 'Please select at least one item to delete.' }
                  ]}
                >
                  <MultiSelectDropdown
                    onChange={onByChange}
                    visible={labelsVisible}
                    setVisible={setLabelsVisible}
                    dropdownSelect={() => {
                      form.setFieldsValue({ by: options.map(({ index }) => index) })
                      setByLength(form.getFieldsValue(true).by.length)
                    }}
                    dropdownClear={() => {
                      form.setFieldsValue({ by: [] })
                      setByLength(0)
                    }}
                    dropdownOk={() => setLabelsVisible(false)}
                    dropdownCancel={() => {
                      form.setFieldsValue({ by: [] })
                      setLabelsVisible(false)
                    }}
                  >
                    {options.map(({ index, name, position }) => (
                      <Select.Option value={index} key={index}>
                        {`${position} - ${name}`}
                      </Select.Option>
                    ))}
                  </MultiSelectDropdown>
                </Form.Item>
              )
            }}
          </Form.Item>
        </Space>
      </Form>
    </CommonModal>
  )
}

export default DeletePosition
